import React from 'react';
import { alpha, Box, Card, CardContent, Stack, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

/**
 * A single headline metric.
 *
 * Every dashboard-style screen was building its own version of this out of
 * Card + Typography, which is why the numbers were different sizes on
 * different pages.
 */
export const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  /** Positive or negative change, already formatted (e.g. "+12.5%"). */
  delta?: string;
  deltaDirection?: 'up' | 'down';
  /** Tints the icon chip. Defaults to the accent. */
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}> = ({ label, value, icon, delta, deltaDirection = 'up', tone = 'primary' }) => (
  <Card
    sx={(t) => ({
      height: '100%',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      // A purely informational card — no onClick anywhere it's used — so this
      // stays a still, non-moving glow rather than a lift/pointer affordance
      // that would wrongly suggest the card is clickable.
      '&:hover': {
        borderColor: alpha(t.palette[tone].main, 0.4),
        boxShadow: `0 0 0 1px ${alpha(t.palette[tone].main, 0.15)}`,
      },
    })}
  >
    <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {icon && (
          <Box
            sx={{
              width: { xs: 38, md: 44 },
              height: { xs: 38, md: 44 },
              flexShrink: 0,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: `${tone}.main`,
              color: `${tone}.contrastText`,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.3 }}
          >
            {label}
          </Typography>
          <Typography
            variant="h5"
            sx={{ mt: 0.25, fontSize: { xs: '1.375rem', md: '1.5rem' } }}
          >
            {value}
          </Typography>
          {delta && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mt: 0.5, minWidth: 0 }}
            >
              {deltaDirection === 'up' ? (
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  minWidth: 0,
                  color: deltaDirection === 'up' ? 'success.main' : 'error.main',
                  // Narrow cards (e.g. a 2-up mobile grid) don't have room for
                  // "+12.5% from last month" on one line, and a title tooltip
                  // is unreachable on touch — wrap instead of truncating.
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  overflow: { xs: 'visible', sm: 'hidden' },
                  textOverflow: { xs: 'clip', sm: 'ellipsis' },
                }}
              >
                {delta}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);
